import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Row, Col } from 'react-bootstrap';
import {
    ASSET_API,
    DEPARTMENT_API,
} from '../utils/apiUrl.js';
import Select from 'react-select';
import { Get } from '../utils/httpProvider';
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const STATUS_COLORS_DEFAULT = ['#4361EE', '#00B4D8', '#2DC653', '#F7B731', '#EF476F', '#9B5DE5', '#F77F00'];

const STATUS_COLOR_MAP = {
    'Chờ phê duyệt': '#F7B731', // Yellow
    'Sẵn sàng': '#2DC653',      // Green
    'Đang sửa chữa': '#EF476F',  // Red
    'Điều chuyển': '#00B4D8',   // Blue/Cyan
};

const getStatusColor = (name, index, backendColor) => {
    if (backendColor) return backendColor;
    if (name && STATUS_COLOR_MAP[name]) return STATUS_COLOR_MAP[name];
    if (name) {
        // Try fuzzy match
        const foundKey = Object.keys(STATUS_COLOR_MAP).find(k => name.includes(k));
        if (foundKey) return STATUS_COLOR_MAP[foundKey];
    }
    return STATUS_COLORS_DEFAULT[index % STATUS_COLORS_DEFAULT.length];
};

const normalizeData = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    return raw.data || raw.Data || [];
};

const selectStyles = {
    control: (base) => ({
        ...base,
        borderRadius: '8px',
        borderColor: '#e0e0e0',
        minHeight: '42px',
        '&:hover': { borderColor: '#4361EE' },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Custom Pie label
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.04) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight="bold">
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

// Custom Tooltip for Bar charts
const BarTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: '#fff', border: '1px solid #eee', padding: '8px 14px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 13 }}>
            <p style={{ fontWeight: 700, margin: '0 0 4px' }}>{label}</p>
            {payload.map((entry, i) => (
                <p key={i} style={{ margin: '2px 0', color: entry.color }}>
                    {entry.name}: <b>{entry.value}</b>
                </p>
            ))}
        </div>
    );
};

const PieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { name, value, payload: innerPayload } = payload[0];
    return (
        <div style={{ background: '#fff', border: '1px solid #eee', padding: '8px 14px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 13 }}>
            <p style={{ fontWeight: 700, margin: '0 0 4px', color: innerPayload.color }}>{name}</p>
            <p style={{ margin: 0 }}>Số lượng: <b>{value}</b></p>
        </div>
    );
};

const cardStyle = {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    border: 'none',
    overflow: 'hidden',
};

const Dashboard = () => {
    const [departmentId, setDepartmentId] = useState(null);

    // ── Queries ──────────────────────────────────────────────────────────────
    const { data: deptsRaw } = useQuery(['getAllDeptsDashboard'], () => Get(DEPARTMENT_API.GET_ALL));
    const departmentOptions = useMemo(() => {
        const list = normalizeData(deptsRaw);
        return [{ value: null, label: 'Toàn bệnh viện' }, ...list.map(d => ({ value: d.id, label: d.departmentName }))];
    }, [deptsRaw]);

    const { data: statusRaw, isLoading: statusLoading } = useQuery(
        ['getStatusStats', departmentId],
        () => Get(departmentId ? `${ASSET_API.GET_STATUS_STATS}?departmentId=${departmentId}` : ASSET_API.GET_STATUS_STATS)
    );
    const { data: deptRaw, isLoading: deptLoading } = useQuery(['getDeptStats'], () => Get(ASSET_API.GET_DEPT_STATS));
    const { data: groupRaw, isLoading: groupLoading } = useQuery(
        ['getGroupStats', departmentId],
        () => Get(departmentId ? `${ASSET_API.GET_GROUP_STATS}?departmentId=${departmentId}` : ASSET_API.GET_GROUP_STATS)
    );

    const statusList = normalizeData(statusRaw);
    const deptList   = normalizeData(deptRaw);
    const groupList  = normalizeData(groupRaw);

    // ── Status Pie Data ──────────────────────────────────────────────────────
    const statusPieData = useMemo(() =>
        statusList.map((x, i) => {
            const name = x.label || x.Label;
            return {
                name: name,
                value: x.value || x.Value,
                color: getStatusColor(name, i, x.color || x.Color),
            };
        }),
    [statusList]);

    // ── Dept Bar Data ────────────────────────────────────────────────────────
    const deptBarData = useMemo(() =>
        deptList.map(x => ({ name: x.label || x.Label, value: x.value || x.Value })),
    [deptList]);

    // ── Group Stacked Bar ─────────────────────────────────────────────────────
    const { groupBarData, statuses } = useMemo(() => {
        if (!groupList.length) return { groupBarData: [], statuses: [] };

        const statusSet = new Map(); // statusName -> color
        const groupMap = {};

        groupList.forEach(x => {
            const gName = x.groupName || x.GroupName;
            const sName = x.statusName || x.StatusName;
            const count  = x.count || x.Count;
            const color  = x.statusColor || x.StatusColor;

            if (!groupMap[gName]) groupMap[gName] = { name: gName };
            groupMap[gName][sName] = (groupMap[gName][sName] || 0) + count;
            if (!statusSet.has(sName)) statusSet.set(sName, color);
        });

        return {
            groupBarData: Object.values(groupMap),
            statuses: [...statusSet.entries()].map(([name, color], i) => ({
                name,
                color: getStatusColor(name, i, color),
            })),
        };
    }, [groupList]);

    const deptChartHeight = Math.max(450, deptList.length * 35);

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="mb-4">
                <h3 style={{ fontWeight: 700, color: '#1a1a1a' }}>Tổng quan tài sản</h3>
                <p className="text-muted mb-0">Hệ thống quản lý trang thiết bị vật tư</p>
            </div>

            {/* Filter */}
            <div style={{ ...cardStyle, padding: '16px 20px', marginBottom: 24 }}>
                <Row className="align-items-center">
                    <Col md="auto" className="mb-2 mb-md-0">
                        <span className="font-weight-bold text-secondary">Lọc khoa phòng:</span>
                        <small className="text-muted ml-2">(áp dụng cho Tình trạng & Nhóm tài sản)</small>
                    </Col>
                    <Col md={4}>
                        <Select
                            styles={selectStyles}
                            options={departmentOptions}
                            value={departmentOptions.find(o => o.value === departmentId) || departmentOptions[0]}
                            onChange={opt => setDepartmentId(opt?.value ?? null)}
                            menuPortalTarget={document.body}
                        />
                    </Col>
                </Row>
            </div>

            <Row>
                {/* ── Pie: Tình trạng tài sản ── */}
                <Col lg={5} className="mb-4">
                    <div style={{ ...cardStyle, height: '100%' }}>
                        <div className="px-4 py-3 border-bottom font-weight-bold">Tình trạng tài sản</div>
                        <div style={{ padding: '16px 8px', height: 400 }}>
                            {statusLoading ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">Đang tải...</div>
                            ) : !statusPieData.length ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">Không có dữ liệu</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={statusPieData}
                                            cx="50%" cy="45%"
                                            innerRadius="45%"
                                            outerRadius="70%"
                                            dataKey="value"
                                            nameKey="name"
                                            labelLine={false}
                                            label={renderPieLabel}
                                            isAnimationActive={true}
                                            animationBegin={100}
                                            animationDuration={900}
                                        >
                                            {statusPieData.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} stroke="#fff" strokeWidth={2} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<PieTooltip />} />
                                        <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 13 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </Col>

                {/* ── Stacked Bar: Nhóm tài sản ── */}
                <Col lg={7} className="mb-4">
                    <div style={{ ...cardStyle, height: '100%' }}>
                        <div className="px-4 py-3 border-bottom font-weight-bold">Phân bổ theo Nhóm tài sản</div>
                        <div style={{ padding: '16px 8px', height: 400 }}>
                            {groupLoading ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">Đang tải...</div>
                            ) : !groupBarData.length ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">Không có dữ liệu</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={groupBarData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                        <Tooltip content={<BarTooltip />} />
                                        <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
                                        {statuses.map(s => (
                                            <Bar
                                                key={s.name}
                                                dataKey={s.name}
                                                stackId="a"
                                                fill={s.color}
                                                isAnimationActive={true}
                                                animationBegin={200}
                                                animationDuration={1000}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </Col>

                {/* ── Horizontal Bar: Khoa phòng ── */}
                <Col lg={12} className="mb-4">
                    <div style={cardStyle}>
                        <div className="px-4 py-3 border-bottom font-weight-bold">
                            Phân bổ theo khoa phòng
                            <span className="text-muted font-weight-normal ml-2" style={{ fontSize: 13 }}>(cuộn để xem thêm)</span>
                        </div>
                        {/* Scrollable wrapper */}
                        <div style={{ overflowX: 'auto' }}>
                            <div style={{ width: `${Math.max(900, deptList.length * 60)}px`, height: 340, padding: '16px 8px' }}>
                                {deptLoading ? (
                                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">Đang tải...</div>
                                ) : !deptBarData.length ? (
                                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">Không có dữ liệu</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={deptBarData} margin={{ top: 10, right: 20, left: 0, bottom: 80 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                            <Tooltip content={<BarTooltip />} />
                                            <Bar
                                                dataKey="value"
                                                name="Số tài sản"
                                                fill="#4361EE"
                                                radius={[4, 4, 0, 0]}
                                                isAnimationActive={true}
                                                animationBegin={300}
                                                animationDuration={1000}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard;
